import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { apiClient } from '../api/client';

// Define types for bank account details
export interface BankAccount {
  id: string;
  accountHolderName: string;
  accountHolderType: string;
  routingNumber: string;
  last4: string;
  bankName?: string;
  country: string;
  currency: string;
  status: string;
}

export interface BankAccountDetails {
  hasBankAccount: boolean;
  bankAccounts: BankAccount[];
}

export const useBankAccount = () => {
  const { currentUser } = useAuth();
  const [bankAccountDetails, setBankAccountDetails] = useState<BankAccountDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch bank account details
  const fetchBankAccountDetails = useCallback(async () => {
    if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
      setBankAccountDetails(null);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.stripe.getBankAccountDetails();
      setBankAccountDetails(response);
    } catch (error) {
      console.error('Error fetching bank account details:', error);
      setError(error as Error);
      setBankAccountDetails(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.stripeDetails?.stripeConnectedAccountId]);

  // Fetch bank account details when user has a connected account ID
  useEffect(() => {
    if (currentUser?.stripeDetails?.stripeConnectedAccountId) {
      fetchBankAccountDetails();
    }
  }, [fetchBankAccountDetails, currentUser?.stripeDetails?.stripeConnectedAccountId]);

  // Extract primary bank account (first one in the list)
  const primaryBankAccount = bankAccountDetails &&
    bankAccountDetails.bankAccounts &&
    bankAccountDetails.bankAccounts.length > 0
      ? bankAccountDetails.bankAccounts[0]
      : null;

  return {
    isLoading,
    error,
    bankAccountDetails,
    hasBankAccount: bankAccountDetails?.hasBankAccount || false,
    bankAccounts: bankAccountDetails?.bankAccounts || [],
    primaryBankAccount,
    refreshBankAccountDetails: fetchBankAccountDetails,
  };
};